import { Formik, Form } from 'formik'
import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'
import styled from 'styled-components'
import { useEventSlug, useQuery } from 'hooks'
import {
    _, withCapture, validation, colors
} from 'utils'
import {
    ProgramTagsQuery,
    ProgramTagsQueryVariables,
    ShowContainerQuery,
    ShowContainerQueryVariables,
} from 'generated/graphql'
import Button from 'components/Button'
import FullPageLoading from 'components/FullPageLoading'
import { GroupedSelectField } from 'components/ReusableStyledComponents'
import SubmitButton from 'features/forms/components/SubmitButton'
import SelectField, { convertGroupedTags } from 'features/forms/components/SelectField'
import TextField from 'features/forms/components/TextField'
import ColorField from 'features/forms/components/ColorField'
import FileUpload, { IMAGE_ONLY } from 'features/forms/components/FileUpload'
import DateField from 'features/forms/components/DateField'
import TimeField from 'features/forms/components/TimeField'
import { showNotification } from 'features/notifications'
import { showErrorNotification } from 'features/notifications/notificationsSlice'
import { allSpacesSelector, mapsSelector, sponsorSelector } from '../events/eventSlice'
import { programTagsQuery, showContainerQuery } from './graphql/query'
import { createShowContainer, updateShowContainer } from './graphql/mutation'
import { convertGroupedOptions } from './tabs/App'

const S = {
    Actions: styled.div`
        display: flex;
        justify-content: flex-end;
        width: 100%;
        padding-right: 10px;
    `,
    Title: styled.p`
        width: 100%;
        padding-left: 10px;
        font-weight: 700;
        font-size: 20px;
        color: ${colors.grayTitleOpacity(0.9)};
    `,
    Description: styled.p`
        width: 100%;
        padding-left: 10px;
        font-size: 14px;
        color: ${colors.grayTitle};
    `,
}

interface Props {
    close?: () => void
    id?: number
    callGetShows?: () => void
}

const createdInitialValues = {
    name: '',
    startDate: new Date(),
    endDate: new Date(),
    color: '#ececec',
    subtitle: '',
    releaseDate: new Date(),
}

const SchemaValidation = Yup.object().shape({
    name: Yup.string().required(),
    subtitle: Yup.string().max(255),
    startDate: Yup.date().required(),
    endDate: validation.endDateProgram,
    playbackUrl: validation.url.nullable(),
})

const CreateUpdateShowContainer = ({ close, id, callGetShows }: Props) => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const sponsors = useSelector(sponsorSelector)
    const allSpaces = useSelector(allSpacesSelector)
    const maps = useSelector(mapsSelector)
    const { data: tagsData, requestStatus } = useQuery<
        ProgramTagsQuery, ProgramTagsQueryVariables
    >(programTagsQuery, {
        eventId: eventSlug,
    })

    const isCreating = !id
    const { data } = useQuery<ShowContainerQuery, ShowContainerQueryVariables>(
        showContainerQuery,
        { id },
        undefined,
        isCreating
    )
    const showContainer = data?.showContainer
    const updatedInitialValues = useMemo(
        () => (showContainer && {
            name: showContainer.name,
            subtitle: showContainer.subtitle,
            description: showContainer.description,
            color: showContainer.color,
            image: showContainer.image,
            startDate: new Date(showContainer.startDate),
            endDate: new Date(showContainer.endDate),
            virtualSpaceId: showContainer.virtualSpace?.id,
            mapitemId: showContainer.mapitem?.id,
            sponsorId: showContainer.sponsor?.id,
            tags: showContainer.tags.map((e) => e.id),
            externalId: showContainer.externalId,
            playbackUrl: showContainer.playbackUrl,
            releaseDate: showContainer.releaseDate
                ? new Date(showContainer.releaseDate)
                : new Date(),
        }),
        [showContainer]
    )

    const onSubmit = withCapture(async (values) => {
        const response = isCreating
            ? await createShowContainer({
                event: eventSlug,
                data: values,
            })
            : await updateShowContainer(
                {
                    id,
                    data: {
                        ...values,
                        image: updatedInitialValues.image === values.image ? undefined : values.image
                    },
                }
            )
        if (response.data) {
            dispatch(showNotification({
                type: 'success',
                body: isCreating
                    ? _('form.notification.create_success')
                    : _('form.notification.update_success'),
            }))
            callGetShows()
            close()
        } else {
            dispatch(showErrorNotification(response.errors))
        }
    })

    return (
        <>
            <Formik
                initialValues={isCreating ? createdInitialValues : updatedInitialValues}
                validationSchema={SchemaValidation}
                onSubmit={onSubmit}
                enableReinitialize
            >
                <Form className="row" id="show-container-form">
                    <TextField
                        name="name"
                        label={_('show_container.form.name')}
                        placeholder={_('show_container.form.name_placeholder')}
                        isRequired
                    />
                    <TextField
                        name="subtitle"
                        label={_('show_container.form.subtitle')}
                        placeholder={_('show_container.form.subtitle_placeholder')}
                    />
                    <TextField
                        name="externalId"
                        label={_('show.form.external_id')}
                        helperText={_('show.form.external_id_helper_text')}
                        showInfoIcon
                        lg={1 / 2}
                    />
                    <SelectField
                        name="sponsorId"
                        label={_('show_container.form.sponsor')}
                        options={sponsors}
                        optionLabel="name"
                        optionValue="id"
                        lg={1 / 2}
                    />
                    <SelectField
                        name="virtualSpaceId"
                        label={_('show_container.form.virtual_space')}
                        options={allSpaces}
                        optionLabel="name"
                        optionValue="id"
                        lg={1 / 2}
                        helperText={_('show_container.form.virtual_space_helper_text')}
                        showInfoIcon
                    />
                    <GroupedSelectField
                        name="mapitemId"
                        label={_('show_container.form.onsite_location')}
                        placeholder={_('show_container.form.onsite_location.placeholder')}
                        helperText={_('show_container.form.onsite_location.helper_text')}
                        options={convertGroupedOptions(maps)}
                        optionLabel="name"
                        optionValue="id"
                        lg={1 / 2}
                        showInfoIcon
                    />
                    <SelectField
                        name="tags"
                        label={_('show_container.form.tags')}
                        options={convertGroupedTags(tagsData?.event?.programTags)}
                        optionLabel="text"
                        optionValue="id"
                        isMulti
                    />
                    <TextField
                        name="description"
                        label={_('show_container.form.description')}
                        placeholder={_('show_container.form.description_placeholder')}
                        isTextArea
                        row={2}
                    />
                    <ColorField
                        name="color"
                        label={_('show_container.form.color')}
                        lg={1/2}
                        listenerWrapperId="show-container-form"
                    />
                    <FileUpload
                        name="image"
                        label={_('show_container.form.image')}
                        placeholder={_('show_container.form.image_placeholder')}
                        accept={IMAGE_ONLY}
                        maxSize={8}
                        lg={1/2}
                        hideLibrary
                    />
                    <DateField
                        name="startDate"
                        isRequired
                        dateFormat="MMM do, yyyy"
                        label={_('show.form.start_date')}
                        lg={1/2}
                        shouldCloseOnSelect={false}
                    />
                    <TimeField
                        name="startDate"
                        isRequired
                        label={_('show.form.start_time')}
                        lg={1/2}
                    />
                    <DateField
                        name="endDate"
                        isRequired
                        dateFormat="MMM do, yyyy"
                        label={_('show.form.end_date')}
                        lg={1/2}
                        shouldCloseOnSelect={false}
                    />
                    <TimeField
                        name="endDate"
                        isRequired
                        label={_('show.form.end_time')}
                        lg={1/2}
                    />

                    <S.Title>{_('show.form.replay_header')}</S.Title>
                    <S.Description>{_('show.form.replay_description')}</S.Description>
                    <TextField
                        name="playbackUrl"
                        label={_('show.form.playback_url')}
                        showInfoIcon
                        helperText={_('show.form.playback_url.helper_text')}
                    />
                    <DateField
                        name="releaseDate"
                        label={_('show.form.releaseDate')}
                        lg={1/2}
                    />
                    <TimeField
                        name="releaseDate"
                        label={_('show.form.releaseTime')}
                        lg={1/2}
                    />

                    <S.Actions>
                        <SubmitButton />
                        <Button variant="secondary" onClick={close}>
                            {_('button.cancel')}
                        </Button>
                    </S.Actions>
                </Form>
            </Formik>
            {requestStatus === 'loading' && (
                <FullPageLoading isAbsolute fullWidth />
            )}
        </>
    )
}

export default CreateUpdateShowContainer
